import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { applyTheme, setCalendarSettings, setCanvasSize, setEditorType, setZoom, setCanvasScale, setSettings } from "../../store/slices/canvas";
import { setAllThemes, setEditorPages, setThemeApplied, setThemeId } from "../../store/slices/projectSetup";
import { GetThemeById, processProjectPages } from "../../library/utils/services/theme/index.js";
import { apiPost } from "../../library/utils/common-services/apiCall.js";
import { ENDPOINTS } from "../../library/utils/constants/apiurl.js";
import { getMaskIds, replaceMaskIdWithPath } from "../../library/utils/common-functions";
import { getMasksByIds } from "../../library/utils/services/mask/index.js";
import { CommonLoader, CommonLoaderContainer } from "../../common-components/StyledComponents";
import { ProductPreview } from "../../products-preview/ProductPreview.jsx";
import MainCanvas from "../../components/canvas/Canvas";
import { Footer } from "../Footer";
import { EDITOR_TYPES } from "../../library/utils/constants";
import { getActiveEditorType, getAllPages, getSettings } from "../../library/utils/helpers";
import { FontProvider } from "../../library/utils/context/FontContext";
import { DesignFontGate } from "../index.jsx";
import { convertToPixels } from "../../library/utils/common-functions/unitConversion";

// Calendar3D / CanvasPrint3D / AcrylicPrint3D all texture themselves from
// `svgData.currentPageSVG`, which only the live editor canvas produces. The canvas
// captures on a 1s timer (Canvas.jsx), so the preview waits for it rather than
// flashing an empty modal. This cap exists so a capture that never lands leaves a
// visible (if untextured) preview instead of a spinner forever.
const TEXTURE_WAIT_MS = 4000;

export const PreviewPage = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(true);
    const [isReady, setIsReady] = useState(false);
    const [textureWaitedOut, setTextureWaitedOut] = useState(false);
    // Collapsed by default: the recipient came to look at the design, so the preview
    // gets the full viewport until they ask for the page list.
    const [showPageStrip, setShowPageStrip] = useState(true);
    // A resolve that fails (reset link, typo, deleted design) has to end somewhere.
    // Without this the page falls through to the loader and spins forever, because
    // `isReady` only ever flips on the success path.
    const [loadError, setLoadError] = useState("");
    const activeEditorType = useSelector(getActiveEditorType);
    const AllPages = useSelector(getAllPages);
    const settings = useSelector(getSettings);
    const currentPageSVG = useSelector((state) => state.svgData.currentPageSVG);

    const themeId = searchParams.get("theme_id");
    const shareCode = searchParams.get("s"); // shared design, view or edit
    const dimensionParam = searchParams.get("dimension"); // e.g. "12x18"
    const unitParam = searchParams.get("unit") || "in"; // default to inches
    const dpiParam = searchParams.get("dpi") || 200; // Optional custom DPI from URL

    useEffect(() => {
        // Shared design: /preview?s=<code>. The backend decides what the code is worth.
        // An edit code hands back only the cart order id, and the visitor is sent into
        // the real editor. A view code hands back an allow-listed copy of the design and
        // nothing that could be turned into edit access, so the id is never in this URL.
        // Reads the SAVED server copy, which is why sharing saves first.
        const loadSharedDesign = async () => {
            const response = await apiPost(ENDPOINTS.viewByShareCode, { share_code: shareCode });
            const items = response?.items;
            if (!items || response.status !== 1) {
                // The backend says the same thing whether the code never existed or was
                // reset, on purpose. Show that message rather than inventing detail.
                setLoadError(response?.message || "This link is no longer available");
                return;
            }

            if (items.mode === "edit") {
                // The share code stays in the URL; the editor resolves it again through
                // useInitializeProject. Handing the recipient the raw c_id would let
                // them keep editing forever even after the owner resets the link.
                // replace: the back button should leave the editor, not bounce the
                // visitor through the resolver again.
                navigate(`/?s=${encodeURIComponent(shareCode)}`, { replace: true });
                return;
            }

            const editorDetails = items.editorDetails;
            if (!editorDetails?.pages_c) {
                setLoadError("This design could not be opened.");
                return;
            }

            dispatch(setZoom(1));
            dispatch(setCanvasScale(1));
            dispatch(setEditorType(editorDetails.editor_type));
            dispatch(setCanvasSize({
                width: editorDetails.width,
                height: editorDetails.height,
                depth: editorDetails.depth || 0,
                safeMargin: editorDetails.safe_margin || 0,
                bleedMargin: editorDetails.bleed_margin || 0,
            }));

            // Photobook stores the full spread width; its pages lay out at half.
            const layoutWidth =
                editorDetails.editor_type === EDITOR_TYPES.PHOTOBOOK
                    ? editorDetails.width / 2
                    : editorDetails.width;
            let pages = processProjectPages(
                editorDetails.pages_c,
                layoutWidth,
                editorDetails.height
            );

            // Saved designs store mask ids, not paths; unresolved masks render blank.
            const maskIds = getMaskIds(pages);
            if (maskIds.length > 0) {
                const maskDetails = await getMasksByIds(maskIds);
                if (maskDetails?.length > 0) {
                    pages = replaceMaskIdWithPath(pages, maskDetails);
                }
            }

            // Keep the saved start month/year: a preview must show what the
            // customer designed, unlike the "reuse this design" path which resets it.
            if (editorDetails.cal_settings) {
                dispatch(setCalendarSettings(editorDetails.cal_settings));
            }
            dispatch(setEditorPages(pages));
            dispatch(setSettings(editorDetails.settings || {}));
            dispatch(applyTheme(pages));
            // The Footer page strip renders skeletons until this is true
            // (Footer.jsx `if (!isThemeApplied)`), so without it the navigator
            // never resolves into thumbnails.
            dispatch(setThemeApplied(true));

            setTimeout(() => setIsReady(true), 500);
        };

        const loadPreviewData = async () => {
            if (shareCode) {
                try {
                    await loadSharedDesign();
                } catch (err) {
                    console.error("Failed to load shared design", err);
                    setLoadError("This link could not be opened. Please try again.");
                } finally {
                    setLoading(false);
                }
                return;
            }

            if (!themeId) {
                setLoading(false);
                return;
            }

            try {
                dispatch(setZoom(1));
                dispatch(setCanvasScale(1));

                let initialCanvasSize = {};
                if (dimensionParam) {
                    const [w, h] = dimensionParam.split("x");
                    if (w && h) {
                        initialCanvasSize = { width: parseFloat(w), height: parseFloat(h) };
                    }
                }

                const themeData = await GetThemeById(
                    themeId,
                    initialCanvasSize, // this helps GetThemeById fetch the right size if backend supports it
                    null, // editorType
                    null, // orientation
                );

                if (themeData && themeData.theme && themeData.theme.length > 0) {
                    dispatch(setEditorType(themeData.editor_type));
                    dispatch(setThemeId(themeId));
                    dispatch(setAllThemes(themeData.theme));

                    // Filter based on dimension and unit if provided
                    let selectedTheme = themeData.theme[0];
                    if (dimensionParam) {
                        const [targetW, targetH] = dimensionParam.split("x").map(Number);

                        if (targetW && targetH) {
                            const matchedTheme = themeData.theme.find(t => {
                                // Priorities for scaling DPI: 1. URL Param -> 2. Theme's DPI -> 3. Fallback (200)
                                const themeDpi = dpiParam ? parseInt(dpiParam, 10) : (parseInt(t.dpi, 10) || 200); 
                                const targetW_px = convertToPixels(targetW, unitParam, themeDpi);
                                const targetH_px = convertToPixels(targetH, unitParam, themeDpi);

                                const themeW = parseFloat(t.width);
                                const themeH = parseFloat(t.height);

                                // Allow a small rounding margin of error (e.g., +/- 5 pixels)
                                const margin = 5;
                                const widthMatches = Math.abs(themeW - targetW_px) <= margin;
                                const heightMatches = Math.abs(themeH - targetH_px) <= margin;
                                const isFlippedMatch = Math.abs(themeW - targetH_px) <= margin && Math.abs(themeH - targetW_px) <= margin;

                                return (widthMatches && heightMatches) || isFlippedMatch;
                            });

                            if (matchedTheme) {
                                selectedTheme = matchedTheme;
                            }
                        }
                    }

                    // set up canvas size
                    let layoutWidth = parseFloat(selectedTheme.width) || 2400;
                    let themeHeight = parseFloat(selectedTheme.height) || 1200;

                    // For layflat album we don't divide by 2, for photobook we might divide by 2 
                    // depend on the scale mechanism in useInitializeProject/useThemeSetup
                    if (themeData.editor_type === EDITOR_TYPES.PHOTOBOOK) {
                        layoutWidth /= 2;
                    }

                    console.log("Canvas Setup:", { layoutWidth, themeHeight, dpi: selectedTheme.dpi, activeEditorType: themeData.editor_type });
                    dispatch(setCanvasSize({
                        width: parseFloat(selectedTheme.width) || 2400, // store actual width in state, although processProjectPages uses layoutWidth
                        height: themeHeight,
                        depth: parseFloat(selectedTheme.depth) || 0,
                        safeMargin: parseFloat(selectedTheme.safe_margin) || 0,
                        bleedMargin: parseFloat(selectedTheme.bleed_margin) || 0,
                        dpi: parseInt(selectedTheme.dpi, 10) || 300,
                    }));

                    const scaledPages = processProjectPages(
                        selectedTheme.pages_c,
                        layoutWidth,
                        themeHeight
                    );

                    // Mark covers if they are in the expected positions for Photobook/Layflat themes
                    if (scaledPages.length >= 2) {
                        if (themeData.editor_type === EDITOR_TYPES.PHOTOBOOK || themeData.editor_type === EDITOR_TYPES.LAYFLATALBUM) {
                            // Ensure first and last pages are flagged as covers for correct rendering in previews
                            scaledPages[0].isCoverPage = true;
                            scaledPages[scaledPages.length - 1].isCoverPage = true;
                            
                            // For Layflat albums, covers are typically half-sheet in the foldable preview
                            if (themeData.editor_type === EDITOR_TYPES.LAYFLATALBUM) {
                                scaledPages[0].settings = { ...(scaledPages[0].settings || {}), isHalfSheet: true };
                                scaledPages[scaledPages.length - 1].settings = { ...(scaledPages[scaledPages.length - 1].settings || {}), isHalfSheet: true };
                            }
                        }
                    }

                    dispatch(setEditorPages(scaledPages));
                    
                    // Fire setSettings BEFORE applyTheme so that if setSettings injects blank covers (standard reducer behavior), 
                    // the subsequent applyTheme will overwrite them with the actual theme content.
                    dispatch(setSettings(selectedTheme.settings || {}));
                    dispatch(applyTheme(scaledPages));
                    // Footer's page strip stays skeletons until this is set.
                    dispatch(setThemeApplied(true));

                    // Allow Redux dispatches and canvas cleanup to propagate before rendering turnkey plugins
                    setTimeout(() => {
                        setIsReady(true);
                    }, 500);
                } else {
                    // Same dead end as an expired share code: without this the theme
                    // route also sits on the spinner forever.
                    setLoadError("This preview is not available");
                }
            } catch (err) {
                console.error("Failed to load preview for theme", err);
                setLoadError("This preview could not be opened. Please try again.");
            } finally {
                setLoading(false);
            }
        };
        loadPreviewData();
    }, [themeId, shareCode, dimensionParam, unitParam, dpiParam, dispatch, navigate]);

    // Starts once the design is in redux, not on mount, so the cap measures the wait
    // for the canvas capture rather than the wait for the network.
    useEffect(() => {
        if (!isReady) return;
        const t = setTimeout(() => setTextureWaitedOut(true), TEXTURE_WAIT_MS);
        return () => clearTimeout(t);
    }, [isReady]);

    if (loading) {
        return (
            <CommonLoaderContainer className="position-absolute w-100 h-100">
                <CommonLoader />
            </CommonLoaderContainer>
        );
    }

    // Checked BEFORE the AllPages guard: the canvas slice ships with a default page, so
    // AllPages is non-empty even when nothing loaded, and a dead link would otherwise
    // fall through to the spinner and never resolve.
    if (loadError) {
        return (
            <div className="d-flex flex-column align-items-center justify-content-center h-100 w-100 position-absolute text-center px-3">
                <h3 style={{ marginBottom: 8 }}>{loadError}</h3>
                <p className="text-muted" style={{ fontSize: 14, maxWidth: 420 }}>
                    The person who shared this design may have replaced or reset the link.
                    Ask them for a new one.
                </p>
            </div>
        );
    }

    if ((!themeId && !shareCode) || !AllPages || AllPages.length === 0) {
        return (
            <div className="d-flex align-items-center justify-content-center h-100 w-100 position-absolute">
                <h3>Preview not available</h3>
            </div>
        );
    }

    // Photobook and the foldable products flip their own pages inside the preview.
    // Everything else renders a single page driven by the active page index, so those
    // need the page strip to be navigable at all. Same split ProductPreview branches on.
    const hasOwnPageNav =
        activeEditorType === EDITOR_TYPES.PHOTOBOOK || settings?.isFoldable === true;

    // Photobook and foldable render their pages straight from redux, so they need
    // NEITHER the offscreen canvas nor the texture wait. Keeping them off that path
    // matters: /preview?theme_id= is embedded by other apps to show theme previews,
    // and those are exactly the two types it serves. They render as immediately as
    // they always did, with no editor canvas mounted behind them.
    const needsPageTexture = !hasOwnPageNav;

    const showPreview = isReady && (!needsPageTexture || currentPageSVG || textureWaitedOut);

    // A single-page product (canvas print, most acrylics) has nothing to navigate to,
    // so the strip is pure clutter there.
    const canNavigatePages = !hasOwnPageNav && AllPages.length > 1;

    return (
        <FontProvider>
            <DesignFontGate />
            <div style={{ width: '100vw', height: '100vh', position: 'relative', overflow: 'hidden' }}>
                {/* Offscreen, NOT display:none. The 3D previews texture from a capture of
                    the live canvas DOM (Canvas.jsx -> getSVGDataToExport(svgRef.current)),
                    and a display:none subtree has zero-size rects, which captures nothing.
                    This canvas is here purely to feed that capture; it is never seen. */}
                {needsPageTexture && (
                    <div
                        aria-hidden="true"
                        style={{
                            position: 'fixed',
                            left: '-10000px',
                            top: 0,
                            width: '1200px',
                            height: '800px',
                            overflow: 'hidden',
                            pointerEvents: 'none',
                        }}
                    >
                        <MainCanvas isToolPanelOpen={false} />
                    </div>
                )}

                {showPreview ? (
                    <ProductPreview show={true} handleClose={() => { }} />
                ) : (
                    <CommonLoaderContainer className="position-absolute w-100 h-100">
                        <CommonLoader />
                    </CommonLoaderContainer>
                )}

                {/* The preview modals own the whole viewport and their close button
                    leads nowhere here (there is no editor behind it), so hide it rather
                    than leave a control that does nothing. Global on purpose: the modals
                    render through a portal at body level, and this route has no other
                    modal to affect. */}
                <style>{`.modal-header .btn-close { display: none !important; }`}</style>

                {/* The previews are fullscreen modals, so the page strip has to sit above
                    them to be reachable. Read-only: it navigates pages, it cannot add or
                    reorder them. */}
                {showPreview && canNavigatePages && (
                    <div
                        style={{
                            position: 'fixed',
                            left: 0,
                            right: 0,
                            bottom: 0,
                            zIndex: 20000,
                            background: '#fff',
                            borderTop: '1px solid #ddd',
                            transform: showPageStrip ? 'none' : 'translateY(100%)',
                            transition: 'transform 0.25s ease',
                        }}
                    >
                        {/* full-width: no editor sidebar here, so ScrollContainer must
                            not reserve 95px for one. */}
                        <Footer readOnly styleclass="full-width" />
                    </div>
                )}

                {showPreview && canNavigatePages && (
                    <button
                        onClick={() => setShowPageStrip((v) => !v)}
                        style={{
                            position: 'fixed',
                            right: 16,
                            bottom: showPageStrip ? 'calc(1rem + 130px)' : 16,
                            zIndex: 20001,
                            border: '1px solid #ddd',
                            background: '#fff',
                            borderRadius: 20,
                            padding: '6px 14px',
                            fontSize: 12,
                            cursor: 'pointer',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                            transition: 'bottom 0.25s ease',
                        }}
                    >
                        {showPageStrip ? "Hide pages" : `Pages (${AllPages.length})`}
                    </button>
                )}
            </div>
        </FontProvider>
    );
};
